/**
 * 1.4 P19: controller equipment view shared by the owner controller detail
 * and the admin inspector — capabilities, relay grid labelled with the
 * actions that use each relay, inputs grid with live levels and functions,
 * buzzer toggle (8RO), and the last program runs / input events.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Switch, AppState, Platform } from 'react-native';

import { useTheme } from '../../context/ThemeContext';
import type { ColorTheme } from '../../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../utils/theme';

export interface PanelClient { get<T = unknown>(path: string): Promise<T>; put<T = unknown>(path: string, body?: unknown): Promise<T> }
interface RelayLabel { action: string; equipment: string; actionId: string }
interface InputCfg { index: number; function: string; activeWhen: string; label: string | null }
export interface PanelController {
  id: string; isOnline: boolean; relayCount?: number; inputCount?: number; capabilities?: string[]; inputsState?: number[]; relayStates?: number[] | null; relayCycles?: number[] | null;
  settings?: Record<string, unknown>; ntpSynced?: boolean; firmwareFamily?: string | null;
  relayLabels?: Record<string, RelayLabel[]>; inputs?: InputCfg[]; equipment?: { kind: string; id: string; name: string; type?: string; live?: Record<string, unknown> }[];
}
interface Run { id: string; state: string; funding: string; createdAt: string; controllerStates?: Record<string, string> }
/** GET /api/controllers/:id/state — the 2 s live poll (2026-10-22). */
export interface LiveState {
  id: string; isOnline: boolean; lastSeen: string | null; relayStates: number[] | null; relayCycles: number[] | null;
  inputsState: number[]; activeProgramId: string | null; equipment: { kind: string; id: string; live: Record<string, unknown> }[]; checkedAt: string;
}
const LIVE_POLL_MS = 2000;

/** True while this page can be seen: foreground on native, visible tab on web. */
function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => (Platform.OS === 'web' && typeof document !== 'undefined' ? document.visibilityState !== 'hidden' : AppState.currentState !== 'background'));
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVis = () => setVisible(document.visibilityState !== 'hidden');
      document.addEventListener('visibilitychange', onVis);
      return () => document.removeEventListener('visibilitychange', onVis);
    }
    const sub = AppState.addEventListener('change', (st) => setVisible(st === 'active'));
    return () => sub.remove();
  }, []);
  return visible;
}
interface InputEvent { id: string; at: string; input: number | null; active: boolean | null }

const FN_LABEL: Record<string, string> = { none: 'Not used', in_operation: 'Wash in progress', out_of_service: 'In service', coin_pulse: 'Coin pulse' };

export default function ControllerEquipmentPanel({ ctrl, client, onSettingsChanged, notify, onLive }: { ctrl: PanelController; client: PanelClient; onSettingsChanged?: () => void; notify?: (type: 'success' | 'error', message: string) => void; onLive?: (s: LiveState) => void }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [events, setEvents] = useState<{ runs: Run[]; inputEvents: InputEvent[] } | null>(null);
  const [buzzer, setBuzzer] = useState<boolean>(!!ctrl.settings?.buzzer);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setBuzzer(!!ctrl.settings?.buzzer); }, [ctrl.settings?.buzzer]);

  const loadEvents = useCallback(async () => {
    try { setEvents(await client.get(`/api/controllers/${ctrl.id}/events`)); } catch { setEvents({ runs: [], inputEvents: [] }); }
  }, [client, ctrl.id]);
  useEffect(() => { loadEvents(); const t = setInterval(loadEvents, 10000); return () => clearInterval(t); }, [loadEvents]);

  // Live relay / input levels: poll the light state endpoint every 2 s while the page is
  // visible. Inputs reach the backend on every edge and relays on every change (firmware
  // 2.1.1 status/relay), so this is what makes the grids move in real time.
  const visible = usePageVisible();
  const [live, setLive] = useState<LiveState | null>(null);
  const [liveErr, setLiveErr] = useState(false);
  const [, setTick] = useState(0);
  // Both callers build `client` and `onLive` inline, so they are new objects every render;
  // keep them in refs and key the effect on the controller id + visibility only, or every
  // parent re-render re-arms the poll (that was an 8 req/s loop in the first dev run).
  const onLiveRef = useRef(onLive); onLiveRef.current = onLive;
  const clientRef = useRef(client); clientRef.current = client;
  useEffect(() => { setLive(null); }, [ctrl.id]);
  useEffect(() => {
    if (!visible) return;
    let stopped = false, inFlight = false;
    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try { const s = await clientRef.current.get<LiveState>(`/api/controllers/${ctrl.id}/state`); if (stopped) return; setLive(s); setLiveErr(false); onLiveRef.current?.(s); }
      catch { if (!stopped) setLiveErr(true); }
      finally { inFlight = false; }
    };
    poll();
    const t = setInterval(poll, LIVE_POLL_MS);
    const tick = setInterval(() => setTick(n => n + 1), 1000);   // "updated Ns ago" text
    return () => { stopped = true; clearInterval(t); clearInterval(tick); };
  }, [ctrl.id, visible]);
  const relayStates = live?.relayStates ?? ctrl.relayStates;
  const relayCycles = live?.relayCycles ?? ctrl.relayCycles;
  const inputsState = live?.inputsState ?? ctrl.inputsState;
  const isOnline = live?.isOnline ?? ctrl.isOnline;
  const liveFor = (id: string) => live?.equipment.find(e => e.id === id)?.live;
  const ageSec = live ? Math.max(0, Math.round((Date.now() - new Date(live.checkedAt).getTime()) / 1000)) : null;
  const liveLabel = !visible ? 'Live updates paused' : liveErr ? 'Live updates unavailable' : ageSec == null ? 'Connecting…' : !isOnline ? 'Board offline · showing last known state' : ageSec <= 2 ? 'Live' : `Live · updated ${ageSec}s ago`;

  const relayCount = ctrl.relayCount || 4;
  const inputCount = ctrl.inputCount || 0;
  const caps = ctrl.capabilities ?? [];
  const toggleBuzzer = async (v: boolean) => {
    setBuzzer(v); setSaving(true);
    try { await client.put(`/api/controllers/${ctrl.id}/settings`, { buzzer: v }); notify?.('success', v ? 'Buzzer on' : 'Buzzer off'); onSettingsChanged?.(); }
    catch (err: any) { setBuzzer(!v); notify?.('error', err.message || 'Could not update the buzzer'); }
    finally { setSaving(false); }
  };

  return (
    <View>
      <Text style={styles.sectionTitle}>Capabilities</Text>
      <View style={styles.chips}>
        {caps.length === 0 && <Text style={styles.muted}>Legacy firmware (single relay commands only)</Text>}
        {caps.map(c => <View key={c} style={styles.chip}><Text style={styles.chipText}>{c}</Text></View>)}
        {ctrl.ntpSynced != null && <View style={[styles.chip, ctrl.ntpSynced ? styles.chipOk : styles.chipWarn]}><Text style={styles.chipText}>{ctrl.ntpSynced ? 'clock synced' : 'clock not synced'}</Text></View>}
      </View>

      <View style={styles.liveRow}>
        <Text style={styles.sectionTitle}>Relays</Text>
        <View style={[styles.livePill, (!visible || liveErr || !isOnline) ? styles.livePillOff : styles.livePillOn]}>
          <View style={[styles.liveDot, { backgroundColor: (visible && !liveErr && isOnline) ? colors.success : colors.textLight }]} />
          <Text style={styles.liveText}>{liveLabel}</Text>
        </View>
      </View>
      <View style={styles.grid}>
        {Array.from({ length: relayCount }, (_, i) => i + 1).map(r => {
          const on = !!(relayStates?.[r - 1]);
          const labels = ctrl.relayLabels?.[String(r)] ?? [];
          return (
            <View key={r} style={[styles.cell, on ? styles.cellOn : styles.cellOff]}>
              <Text style={[styles.cellLabel, on && styles.cellLabelOn]}>R{r}</Text>
              <Text style={[styles.cellState, on && styles.cellLabelOn]}>{on ? 'ON' : 'OFF'}</Text>
              <Text style={styles.cellText} numberOfLines={3}>{labels.length ? [...new Set(labels.map(l => `${l.action} (${l.equipment})`))].join(', ') : 'unassigned'}</Text>
              {relayCycles?.[r - 1] != null && <Text style={styles.cellMeta}>{relayCycles[r - 1]} cycles</Text>}
            </View>
          );
        })}
      </View>

      {inputCount > 0 && (
        <>
          <Text style={styles.sectionTitle}>Inputs</Text>
          <View style={styles.grid}>
            {Array.from({ length: inputCount }, (_, i) => i + 1).map(n => {
              const lvl = inputsState?.[n - 1];
              const cfg = ctrl.inputs?.find(i => i.index === n);
              const active = cfg && cfg.function !== 'none' && lvl != null ? (cfg.activeWhen === 'no_signal' ? lvl === 0 : lvl === 1) : false;
              return (
                <View key={n} style={[styles.cell, active ? styles.cellActive : styles.cellOff]}>
                  <View style={styles.inHead}>
                    <View style={[styles.dot, { backgroundColor: lvl === 1 ? colors.primary : lvl === 0 ? colors.borderLight : colors.textLight }]} />
                    <Text style={styles.cellLabel}>I{n}</Text>
                  </View>
                  <Text style={styles.cellText}>{cfg ? FN_LABEL[cfg.function] ?? cfg.function : 'Not used'}</Text>
                  {cfg && cfg.function !== 'none' && <Text style={styles.cellMeta}>{cfg.activeWhen === 'no_signal' ? 'no signal = active' : 'signal = active'}{active ? ' · ACTIVE' : ''}</Text>}
                  {!!cfg?.label && <Text style={styles.cellMeta}>{cfg.label}</Text>}
                </View>
              );
            })}
          </View>
        </>
      )}

      {caps.includes('buzzer') && (
        <View style={styles.switchRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.switchLabel}>Buzzer</Text>
            <Text style={styles.muted}>Short beep when a program starts. Saved on the board.</Text>
          </View>
          <Switch value={buzzer} disabled={saving} onValueChange={toggleBuzzer} trackColor={{ false: colors.borderLight, true: colors.primary + '80' }} thumbColor={buzzer ? colors.primary : colors.textLight} />
        </View>
      )}

      {!!ctrl.equipment?.length && (
        <>
          <Text style={styles.sectionTitle}>Serves</Text>
          <View style={styles.card}>
            {ctrl.equipment.map(e => {
              const live = (liveFor(e.id) ?? e.live ?? {}) as { inUse?: boolean; outOfService?: boolean };
              return <Text key={e.id} style={styles.line}>{e.kind === 'vending' ? 'Vending' : e.type ?? 'Bay'} · {e.name}{live.outOfService ? ' · OUT OF SERVICE' : live.inUse ? ' · in use' : ''}</Text>;
            })}
          </View>
        </>
      )}

      <Text style={styles.sectionTitle}>Recent program runs</Text>
      <View style={styles.card}>
        {!events && <Text style={styles.muted}>Loading…</Text>}
        {events && events.runs.length === 0 && <Text style={styles.muted}>No program runs yet.</Text>}
        {events?.runs.map(r => (
          <View key={r.id} style={styles.evRow}>
            <Text style={[styles.evState, r.state === 'done' && { color: colors.success }, ['timeout', 'error'].includes(r.state) && { color: colors.danger }]}>{r.state}</Text>
            <Text style={styles.evText} numberOfLines={1}>{r.funding} · {r.id}</Text>
            <Text style={styles.evTime}>{new Date(r.createdAt).toLocaleTimeString()}</Text>
          </View>
        ))}
      </View>
      {inputCount > 0 && (
        <>
          <Text style={styles.sectionTitle}>Recent input events</Text>
          <View style={styles.card}>
            {events && events.inputEvents.length === 0 && <Text style={styles.muted}>No input changes logged in the last 7 days.</Text>}
            {events?.inputEvents.map(e => (
              <View key={e.id} style={styles.evRow}>
                <Text style={styles.evState}>I{e.input ?? '?'}</Text>
                <Text style={styles.evText}>{e.active === true ? 'signal' : e.active === false ? 'no signal' : '—'}</Text>
                <Text style={styles.evTime}>{new Date(e.at).toLocaleString()}</Text>
              </View>
            ))}
          </View>
        </>
      )}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  liveRow: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: spacing.sm },
  livePill: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: spacing.sm, paddingVertical: 3, borderRadius: 12, borderWidth: 1, marginBottom: spacing.sm },
  livePillOn: { borderColor: c.success, backgroundColor: c.success + '15' },
  livePillOff: { borderColor: c.border, backgroundColor: c.surfaceSecondary },
  liveDot: { width: 8, height: 8, borderRadius: 4 },
  liveText: { fontSize: 11, color: c.text, fontWeight: fontWeight.medium },
  card: { backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md, borderWidth: 1, borderColor: c.borderLight },
  muted: { fontSize: fontSize.xs, color: c.textSecondary },
  line: { fontSize: fontSize.sm, color: c.text, paddingVertical: 2 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: 3, borderRadius: 12, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  chipOk: { borderColor: c.success, backgroundColor: c.success + '15' },
  chipWarn: { borderColor: c.warning, backgroundColor: c.warning + '15' },
  chipText: { fontSize: 11, color: c.text, fontWeight: fontWeight.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  cell: { width: 112, minHeight: 72, padding: spacing.xs, borderRadius: borderRadius.md, borderWidth: 1 },
  cellOff: { backgroundColor: c.surface, borderColor: c.borderLight },
  cellOn: { backgroundColor: '#dc262615', borderColor: '#dc2626' },
  cellActive: { backgroundColor: c.primary + '15', borderColor: c.primary },
  cellLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: c.text },
  cellLabelOn: { color: '#dc2626' },
  cellState: { fontSize: 10, fontWeight: fontWeight.semibold, color: c.textSecondary },
  cellText: { fontSize: 11, color: c.text, marginTop: 2 },
  cellMeta: { fontSize: 10, color: c.textSecondary, marginTop: 2 },
  inHead: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  dot: { width: 10, height: 10, borderRadius: 5 },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginTop: spacing.lg, padding: spacing.md, backgroundColor: c.surface, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  switchLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  evRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: 4, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: c.borderLight },
  evState: { width: 64, fontSize: 11, fontWeight: fontWeight.semibold, color: c.textSecondary },
  evText: { flex: 1, fontSize: 11, color: c.text },
  evTime: { fontSize: 10, color: c.textSecondary },
});
